import React, { useState, useRef, useEffect } from 'react';
import {
  Heart, Activity, Zap, Camera, AlertCircle,
  Clock, TrendingUp, ChevronRight, User, Stethoscope,
} from 'lucide-react';
import './HealthMonitorApp.css';
import { useNavigate, useLocation } from 'react-router-dom';

const HealthMonitorApp = () => {
  // ─── Flow: idle → prefill → camera → recording → processing → (navigate)
  const [stage, setStage] = useState('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);

  const navigate = useNavigate();
  const { state: routeState } = useLocation();

  // ─── Patient info + optional ground truth — filled BEFORE scan ──────────────
  const [actualValues, setActualValues] = useState({
    patient_name: '',
    patient_age: '',
    actual_heart_rate: '',
    actual_sbp: '',
    actual_dbp: '',
    notes: '',
  });

  // Pre-fill from route state if navigated from a patient list
  useEffect(() => {
    if (routeState?.patientName || routeState?.patientAge) {
      setActualValues(prev => ({
        ...prev,
        patient_name: routeState.patientName || '',
        patient_age:  routeState.patientAge  || '',
      }));
    }
  }, [routeState]);

  const videoRef         = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);

  // ─── Camera ─────────────────────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: false,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(e => console.error('Video play error:', e));
      }
    } catch (err) {
      setError('Could not access camera. Please check permissions.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  // ─── Recording ──────────────────────────────────────────────────────────────
  const startRecording = () => {
    if (!stream) {
      setError('Camera stream not available. Please restart camera.');
      return;
    }
    setError(null);
    chunksRef.current = [];

    const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9' });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      uploadVideo(blob);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setStage('recording');
    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const next = prev + 1;
        if (next >= 30) {
          stopRecording();
          clearInterval(timerRef.current);
        }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // ─── Upload: video + all form fields in ONE request, then navigate ───────────
  // const uploadVideo = async (blob) => {
  //   setStage('processing');
  //   setProgress(0);

  //   const progressInterval = setInterval(() => {
  //     setProgress(prev => Math.min(prev + 10, 90));
  //   }, 500);

  //   try {
  //     const formData = new FormData();
  //     formData.append('video', blob, 'recording.webm');

  //     // scan_user
  //     // const scanUser = actualValues.patient_name || routeState?.patientName || '';
  //     // if (scanUser) formData.append('scan_user', scanUser);

  //     // technician email from local storage
  //     const technicianEmail = localStorage.getItem('technicianEmail');
  //     if (technicianEmail) formData.append('scan_user', technicianEmail);

  //     // Optional ground truth fields — only append if not empty
  //     if (actualValues.patient_name)      formData.append('patient_name',      actualValues.patient_name);
  //     if (actualValues.patient_age)       formData.append('patient_age',       actualValues.patient_age);
  //     if (actualValues.actual_heart_rate) formData.append('actual_heart_rate', actualValues.actual_heart_rate);
  //     if (actualValues.actual_sbp)        formData.append('actual_sbp',        actualValues.actual_sbp);
  //     if (actualValues.actual_dbp)        formData.append('actual_dbp',        actualValues.actual_dbp);
  //     if (actualValues.notes)             formData.append('notes',             actualValues.notes);

  //     const response = await fetch(
  //       'http://127.0.0.1:8000/api/vitals/analyze-vitals/',
  //       { method: 'POST', body: formData }
  //     );

  //     // const response = await fetch(
  //     //   'https://catbackend.xraidigital.com/api/vitals/analyze-vitals/',
  //     //   { method: 'POST', body: formData }
  //     // );

  //     clearInterval(progressInterval);
  //     setProgress(100);

  //     const data = await response.json();
  //     console.log('API Response:', data);

  //     if (data.success && data.data) {
  //       const apiData = data.data;
  //       stopCamera();

  //       // ── Navigate immediately — no results screen ─────────────────────────
  //       navigate(`/medibuddy-form/${routeState?.patientId}`, {
  //         state: {
  //           unique_patient_id: routeState?.unique_patient_id,
  //           name: routeState?.patientName || actualValues.patient_name,
  //           age:  routeState?.patientAge  || actualValues.patient_age,
  //           autoFillVitals: {
  //             blood_pressure: `${actualValues.actual_sbp || apiData.bp_systolic}/${actualValues.actual_dbp || apiData.bp_diastolic}`,
  //             heart_rate:      actualValues.actual_heart_rate || apiData.heart_rate || '',
  //           },
  //           scanResults: {
  //             heart_rate:       apiData.heart_rate       || 0,
  //             bp_systolic:      apiData.bp_systolic      || 0,
  //             bp_diastolic:     apiData.bp_diastolic     || 0,
  //             hrv_sdnn:         apiData.hrv_sdnn         || 0,
  //             hrv_rmssd:        apiData.hrv_rmssd        || 0,
  //             spo2:             apiData.spo2             || null,
  //             resp_rate:        apiData.resp_rate        || null,
  //             hemoglobin:       apiData.hemoglobin       || null,
  //             confidence:       apiData.confidence       || 0,
  //             duration:         apiData.duration         || 0,
  //             frames_analyzed:  apiData.frames_analyzed  || 0,
  //             scan_id:          apiData.id               || 0,
  //             training_data_id: apiData.training_data_id || 0,
  //           },
  //         },
  //       });

  //     } else {
  //       // On failure — go back to prefill so user can retry without re-entering data
  //       setError(data.error || 'Analysis failed');
  //       setStage('prefill');
  //     }

  //   } catch (err) {
  //     clearInterval(progressInterval);
  //     setError('Could not connect to server. Make sure Django backend is running.');
  //     setStage('prefill');
  //   }
  // };


  // NEW
const uploadVideo = (blob) => {
  // ── Navigate immediately with whatever we already know ─────────────────
  stopCamera();

  navigate(`/medibuddy-form/${routeState?.patientId}`, {
    state: {
      unique_patient_id: routeState?.unique_patient_id,
      name: routeState?.patientName || actualValues.patient_name,
      age:  routeState?.patientAge  || actualValues.patient_age,
      autoFillVitals: {
        blood_pressure: actualValues.actual_sbp && actualValues.actual_dbp
          ? `${actualValues.actual_sbp}/${actualValues.actual_dbp}`
          : null,
        heart_rate: actualValues.actual_heart_rate || '',
      },
    },
  });

  // ── Fire-and-forget upload in background ───────────────────────────────
  const formData = new FormData();
  formData.append('video', blob, 'recording.webm');

  const technicianEmail = localStorage.getItem('technicianEmail');
  if (technicianEmail) formData.append('scan_user', technicianEmail);

  if (actualValues.patient_name)      formData.append('patient_name',      actualValues.patient_name);
  if (actualValues.patient_age)       formData.append('patient_age',       actualValues.patient_age);
  if (actualValues.actual_heart_rate) formData.append('actual_heart_rate', actualValues.actual_heart_rate);
  if (actualValues.actual_sbp)        formData.append('actual_sbp',        actualValues.actual_sbp);
  if (actualValues.actual_dbp)        formData.append('actual_dbp',        actualValues.actual_dbp);
  if (actualValues.notes)             formData.append('notes',             actualValues.notes);

  fetch('https://catbackend.xraidigital.com/api/vitals/analyze-vitals/', {
    method: 'POST',
    body: formData,
  })
    .then(res => res.json())
    .then(data => console.log('Background upload complete:', data))
    .catch(err => console.error('Background upload failed:', err));
};
//   fetch('http://127.0.0.1:8000/api/vitals/analyze-vitals/', {
//     method: 'POST',
//     body: formData,
//   })
//     .then(res => res.json())
//     .then(data => console.log('Background upload complete:', data))
//     .catch(err => console.error('Background upload failed:', err));
// };

  // ─── Reset ───────────────────────────────────────────────────────────────────
  const reset = () => {
    setStage('idle');
    setRecordingTime(0);
    setError(null);
    stopCamera();
    setActualValues({
      patient_name:      routeState?.patientName || '',
      patient_age:       routeState?.patientAge  || '',
      actual_heart_rate: '',
      actual_sbp:        '',
      actual_dbp:        '',
      notes:             '',
    });
  };

  const handleProceedToCamera = async () => {
    setError(null);
    await startCamera();
    setStage('camera');
  };

  const handleChange = (field) => (e) =>
    setActualValues(prev => ({ ...prev, [field]: e.target.value }));

  // Sync video element when stream changes
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().catch(e => console.error('Video play error:', e));
      };
    }
  }, [stream]);

  useEffect(() => {
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="health-monitor-app">
      <div className="app-container">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="header">
          <div className="logo-container">
            <div className="logo-icon">
              <Heart className="heart-icon" />
              <Heart className="heart-icon-ping" />
            </div>
            <h1 className="app-title">VitalScan</h1>
          </div>
          <p className="app-subtitle">AI-Powered Health Monitoring from Your Camera</p>
          <div className="status-indicator">
            <div className="status-dot"></div>
            <p className="status-text">Real-time rPPG Analysis</p>
          </div>
        </div>

        {/* ── Error banner ────────────────────────────────────────────────── */}
        {error && (
          <div className="error-message">
            <AlertCircle className="error-icon" />
            <div>
              <p className="error-title">Error</p>
              <p className="error-detail">{error}</p>
            </div>
          </div>
        )}

        <div className="main-card">

          {/* ════════════════════════════════════════════════════════════════
              STAGE 1: idle
          ════════════════════════════════════════════════════════════════ */}
          {stage === 'idle' && (
            <div className="idle-state">
              <div className="idle-content">
                <div className="camera-icon-container">
                  <div className="camera-icon-background"></div>
                  <Camera className="camera-icon" />
                </div>
                <h2 className="idle-title">Ready to Analyze Your Vitals</h2>
                <p className="idle-description">
                  Position your face in the frame and remain still for 30 seconds.
                  Ensure you're in a well-lit environment for accurate results.
                </p>
              </div>

              <button onClick={() => setStage('prefill')} className="start-button">
                <span className="button-content">
                  <Camera className="button-icon" />
                  Start Scan
                </span>
                <div className="button-hover"></div>
              </button>

              <div className="features-grid">
                <div className="feature-card feature-card-1">
                  <Heart className="feature-icon" />
                  <h3 className="feature-title">Heart Rate</h3>
                  <p className="feature-description">Real-time BPM tracking</p>
                </div>
                <div className="feature-card feature-card-2">
                  <Activity className="feature-icon" />
                  <h3 className="feature-title">HRV Metrics</h3>
                  <p className="feature-description">Variability analysis</p>
                </div>
                <div className="feature-card feature-card-3">
                  <TrendingUp className="feature-icon" />
                  <h3 className="feature-title">Blood Pressure</h3>
                  <p className="feature-description">Systolic &amp; Diastolic</p>
                </div>
                <div className="feature-card feature-card-4">
                  <Zap className="feature-icon" />
                  <h3 className="feature-title">AI Analysis</h3>
                  <p className="feature-description">Instant processing</p>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              STAGE 2: prefill — patient info + ground truth BEFORE scan
          ════════════════════════════════════════════════════════════════ */}
          {stage === 'prefill' && (
<div className="prefill-state">
  <div className="prefill-card">
    
    {/* Header */}
    <div className="prefill-header">
      <div className="prefill-icon-wrap">
        <User className="prefill-icon" />
      </div>

      <div>
        <h2 className="prefill-title">Patient Information</h2>
        <p className="prefill-subtitle">
          Enter patient details and optional medical readings before starting the scan.
        </p>
      </div>
    </div>

    {/* Patient Details */}
    <div className="prefill-section">
      <div className="prefill-section-label">
        <User size={15} />
        <span>Patient Details</span>
      </div>

      <div className="ground-truth-inputs two-column-grid">
        <div className="input-group">
          <label className="input-label">Patient Name</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter patient name"
            value={actualValues.patient_name}
            onChange={handleChange('patient_name')}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Patient Age</label>

          <div className="input-with-unit">
            <input
              type="number"
              className="input-field"
              placeholder="Enter age"
              value={actualValues.patient_age}
              onChange={handleChange('patient_age')}
              min="0"
              max="150"
            />
            <span className="input-unit">Years</span>
          </div>
        </div>
      </div>
    </div>

    {/* Medical Readings */}
    <div className="prefill-section">
      <div className="prefill-section-label">
        <Stethoscope size={15} />
        <span>Actual Medical Device Readings</span>

        <span className="optional-tag">(Optional)</span>
      </div>

      <div className="ground-truth-inputs vitals-grid">
        <div className="input-group">
          <label className="input-label">Heart Rate</label>

          <div className="input-with-unit">
            <input
              type="number"
              className="input-field"
              placeholder="72"
              value={actualValues.actual_heart_rate}
              onChange={handleChange('actual_heart_rate')}
            />
            <span className="input-unit">BPM</span>
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Systolic BP</label>

          <div className="input-with-unit">
            <input
              type="number"
              className="input-field"
              placeholder="120"
              value={actualValues.actual_sbp}
              onChange={handleChange('actual_sbp')}
            />
            <span className="input-unit">mmHg</span>
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Diastolic BP</label>

          <div className="input-with-unit">
            <input
              type="number"
              className="input-field"
              placeholder="80"
              value={actualValues.actual_dbp}
              onChange={handleChange('actual_dbp')}
            />
            <span className="input-unit">mmHg</span>
          </div>
        </div>

        <div className="input-group full-width">
          <label className="input-label">Notes</label>

          <textarea
            className="input-field textarea-field"
            placeholder="e.g. Omron device, seated, calm"
            value={actualValues.notes}
            onChange={handleChange('notes')}
            rows="3"
          />
        </div>
      </div>
    </div>

    {/* Buttons */}
    <div className="prefill-actions">
      <button onClick={handleProceedToCamera} className="prefill-start-button">
        <span className="button-content">
          <Camera className="button-icon" />
          Proceed to Camera
          <ChevronRight size={16} style={{ marginLeft: 6 }} />
        </span>
      </button>

      <button
        onClick={() => setStage('idle')}
        className="prefill-cancel-button"
      >
        ← Back
      </button>
    </div>
  </div>
</div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              STAGE 3 & 4: camera / recording
          ════════════════════════════════════════════════════════════════ */}
          {(stage === 'camera' || stage === 'recording') && (
            <div className={stage === 'camera' ? 'camera-state' : 'recording-state'}>
              <div className={stage === 'camera' ? 'video-container' : 'recording-video-container'}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  width="1280"
                  height="720"
                  className={stage === 'camera' ? 'video-preview' : 'recording-video'}
                  style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
                />

                {stage === 'camera' && (
                  <>
                    <div className="video-overlay">
                      <div className="face-guide">
                        <div className="face-guide-circle"></div>
                      </div>
                    </div>
                    <div className="camera-status">
                      <div className="status-indicator-dot"></div>
                      <span className="status-text">Camera Active</span>
                    </div>
                  </>
                )}

                {stage === 'recording' && (
                  <>
                    <div className="recording-indicator">
                      <div className="recording-dot-active"></div>
                      Recording
                    </div>
                    <div className="recording-timer">
                      <Clock className="timer-icon" />
                      <span className="timer-value">{recordingTime}s</span>
                      <span className="timer-total"> / 30s</span>
                    </div>
                  </>
                )}
              </div>

              {stage === 'camera' && (
                <div className="camera-controls">
                  <p className="camera-instructions">
                    Center your face in the oval guide and click "Start Recording"
                  </p>
                  <div className="control-buttons">
                    <button onClick={startRecording} className="record-button">
                      <div className="recording-dot"></div>
                      Start Recording
                    </button>
                    <button onClick={reset} className="cancel-button">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {stage === 'recording' && (
                <>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${(recordingTime / 30) * 100}%` }}
                    ></div>
                  </div>
                  <p className="recording-instructions">Stay still and breathe normally...</p>
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              STAGE 5: processing
              On success → navigate() fires immediately, no results stage
          ════════════════════════════════════════════════════════════════ */}
          {/* {stage === 'processing' && (
            <div className="processing-state">
              <div className="processing-content">
                <Activity className="processing-icon" />
                <h2 className="processing-title">Analyzing Your Vitals...</h2>
                <p className="processing-description">Our AI is processing your video</p>
              </div>
              <div className="processing-progress">
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-processing"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="progress-text">{progress}%</p>
              </div>
            </div>
          )} */}

        </div>{/* /main-card */}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="footer">
          <div className="footer-badge">
            <Activity style={{ width: '1rem', height: '1rem' }} />
            <p className="footer-text">Powered by U4rad Technology + AI</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HealthMonitorApp;